// Browser bootstrap for E2E: wires the IndexedDB store + the WASM loader into
// the manager, and exposes a console self-test so the crypto can be verified in
// the actual browser before it is wired into the message flow.

import { createIdbStore } from './store-idb.js';
import { ozcryptoFactory } from './load-web.js';
import { createE2E } from './e2e.js';
import { generateIdentity, generatePrekeys, publicBundle } from './x3dh.js';
import { startSession, acceptSession, encrypt as sesEncrypt, decrypt as sesDecrypt } from './session.js';
import { loadAead } from './aead.js';

let managerPromise = null;
let manager = null; // resolved manager, for synchronous access in the render path
let currentUid = null;

// The user's own E2E manager (identity + sessions persisted in IndexedDB).
export function initE2E(uid) {
  // Account switch (login as a different user without a full reload): drop the
  // previous user's manager so a fresh one is built against the NEW user's own
  // IndexedDB. Never reuse another account's identity/sessions/plaintext cache.
  if (currentUid && currentUid !== uid) {
    managerPromise = null;
    manager = null;
  }
  currentUid = uid;
  if (!managerPromise) {
    managerPromise = (async () => {
      // NOTE: the ?e2ereset URL trigger was REMOVED (security): a crafted link could
      // silently wipe a user's E2E keys/sessions, forcing a re-handshake (DoS, and it
      // could aid a MITM). E2E reset must be a deliberate in-app action only.
      const store = await createIdbStore(uid);
      const factory = await ozcryptoFactory();
      manager = await createE2E(store, factory);
      return manager;
    })();
  }
  return managerPromise;
}

// Synchronous accessor (null until initE2E resolves).
export function getE2E() { return manager; }

// Wipe this device's DECRYPTED message cache on logout (audit 2nd tier). Keeps the
// identity/prekeys/sessions so re-login doesn't regenerate the identity (which would
// force every peer to re-verify), but removes readable history — making the SDK's
// clearStorage() on logout actually effective.
export async function wipeE2EPlaintext() {
  if (manager && typeof manager.wipePlaintext === 'function') {
    try { await manager.wipePlaintext(); } catch (e) { /* ignore */ }
  }
}

// AEAD associated data bound to each P2P message: the unordered participant pair
// plus the message author (КР-7). Symmetric on both sides regardless of which
// side names the topic how.
export function e2eAad(uidA, uidB, authorUid) {
  const pair = [uidA, uidB].sort().join(':');
  return new TextEncoder().encode(pair + '|' + authorUid);
}

// AAD binding a group message body (ФТ-9) to its topic and author, so a group
// ciphertext cannot be replayed into another topic or attributed to another
// sender. Both sides compute it identically from the message's own from/topic.
export function e2eGroupContentAad(topicName, senderUid) {
  return new TextEncoder().encode('grp|' + topicName + '|' + senderUid);
}

// AAD for the per-member wrapped content key: it travels the same pairwise
// channel a 1-on-1 message would, so it reuses the exact p2p binding
// (sender<->member pair + author=sender). Symmetric on both ends.
export function e2eGroupWrapAad(senderUid, memberUid) {
  return e2eAad(senderUid, memberUid, senderUid);
}

// Self-test runnable from the browser console: window.nexusE2ETest().
// Runs a full Alice<->Bob handshake + ratchet + tamper check entirely in the
// browser, on the real WASM core. Logs PASS/FAIL for each step.
async function selfTest() {
  const enc = (s) => new TextEncoder().encode(s);
  const dec = (b) => (b ? new TextDecoder().decode(b) : null);
  const aead = await loadAead(await ozcryptoFactory()); // gates on oz_selftest
  let ok = true;
  const step = (name, cond) => { ok = ok && cond; console.log(`[E2E] ${cond ? 'PASS' : 'FAIL'} ${name}`); };

  const bob = generateIdentity(); const bobPk = generatePrekeys(bob, 4);
  const alice = generateIdentity();
  const { session: aSes, init } = startSession(alice, publicBundle(bob, bobPk, 0));
  const aad = enc('p2pTest|alice');
  const w1 = sesEncrypt(aSes, enc('привет из браузера 🔐'), aad, aead);
  const bSes = acceptSession(bob, bobPk, init);
  step('handshake + first message', dec(sesDecrypt(bSes, w1.header, w1.ct, w1.tag, aad, aead)) === 'привет из браузера 🔐');

  const r = sesEncrypt(bSes, enc('ответ'), enc('p2pTest|bob'), aead);
  step('reply (DH ratchet)', dec(sesDecrypt(aSes, r.header, r.ct, r.tag, enc('p2pTest|bob'), aead)) === 'ответ');

  const t = sesEncrypt(aSes, enc('целостность'), aad, aead);
  const bad = t.ct.slice(); bad[0] ^= 1;
  step('tamper -> null', sesDecrypt(bSes, t.header, bad, t.tag, aad, aead) === null);

  console.log(`[E2E] self-test: ${ok ? 'ALL PASS ✅' : 'FAILURES ❌'}`);
  return ok;
}

// Expose for manual verification. Safe to call anytime; does not touch chats.
if (typeof window !== 'undefined') {
  window.nexusE2ETest = selfTest;
  // Wipe this account's E2E state (identity, prekeys, sessions) and reload — used
  // to recover from a desynced session during testing. A fresh identity + bundle
  // is generated and republished on the next login.
  window.nexusE2EReset = () => {
    if (currentUid) {
      indexedDB.deleteDatabase('nexus-e2e:' + currentUid);
    }
    console.log('[Nexus E2E] state cleared — reload both sides, then retry');
    setTimeout(() => location.reload(), 300);
  };
}
